import { dom } from './dom'
import { getAppLocale as coreGetAppLocale, getSystemLocale as coreGetSystemLocale } from './core/utils.js'

/** App globals. */
export const app = {
  activePlot: null,
  activePage: null,
  colors: null,
  currencies: {},
  iconCache: {},
  mathScope: new Map(),
  scopeProperties: {},
  scopeTypes: {},
  plotFunction: null,
  plotSettings: null,
  refreshCM: true,
  settings: null,
  sidebarDocked: null,
  udfList: [],
  uduList: [],
  widgetMap: new WeakMap()
}

let cachedConsolidatedRaw = null
let cachedConsolidatedObj = null

function getConsolidatedStore() {
  const raw = localStorage.getItem('numara')

  if (!raw) {
    cachedConsolidatedRaw = null
    cachedConsolidatedObj = null
    return null
  }

  if (raw !== cachedConsolidatedRaw || !cachedConsolidatedObj) {
    cachedConsolidatedRaw = raw
    cachedConsolidatedObj = JSON.parse(raw)
  }

  return cachedConsolidatedObj
}

function saveConsolidatedStore(obj) {
  const raw = JSON.stringify(obj)

  cachedConsolidatedRaw = raw
  cachedConsolidatedObj = obj
  localStorage.setItem('numara', raw)
}

/** Local storage utility. */
export const store = {
  /**
   * Get value from local storage.
   * @param {string} key The key of the item to retrieve.
   * @returns {any} The parsed value from local storage.
   */
  get: (key) => {
    try {
      const obj = getConsolidatedStore()

      if (obj && obj[key] !== undefined) {
        const val = obj[key]

        return val !== null && typeof val === 'object' ? structuredClone(val) : val
      }

      const rootItem = localStorage.getItem(key)

      if (rootItem) {
        const parsed = JSON.parse(rootItem)

        store.set(key, parsed)
        localStorage.removeItem(key)

        return parsed
      }

      return null
    } catch (error) {
      console.error(`Error parsing local storage for key "${key}":`, error)

      return null
    }
  },

  /**
   * Save value to local storage.
   * @param {string} key The key under which to store the value.
   * @param {any} value The value to store.
   */
  set: (key, value) => {
    try {
      const obj = getConsolidatedStore() || {}

      obj[key] = value !== null && typeof value === 'object' ? structuredClone(value) : value
      saveConsolidatedStore(obj)
    } catch (error) {
      console.error(`Error writing key "${key}" to local storage:`, error)
    }
  },

  /**
   * Remove value from local storage.
   * @param {string} key The key of the item to remove.
   */
  remove: (key) => {
    try {
      localStorage.removeItem(key)

      const obj = getConsolidatedStore()

      if (obj) {
        delete obj[key]
        saveConsolidatedStore(obj)
      }
    } catch (error) {
      console.error(`Error removing key "${key}" from local storage:`, error)
    }
  }
}

const userAgent = navigator.userAgent.toLowerCase()
const DEFAULT_APP_WIDTH = 560
const DEFAULT_APP_HEIGHT = 480

/** Check if app is running in Electron. */
export const isElectron = typeof window !== 'undefined' && window.numara !== undefined

/** Check if app is running on MacOS. */
export const isMac = isElectron ? window.numara.isMac : userAgent.includes('mac')
/** Check if app is running on Windows. */
export const isWindows = isElectron ? window.numara.isWindows : userAgent.includes('win')
/** Check if app is running on Linux. */
export const isLinux = isElectron ? window.numara.isLinux : userAgent.includes('linux')
/** Check if app is running on a mobile device. */
export const isMobile =
  /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent) ||
  (typeof navigator !== 'undefined' && !!navigator.userAgentData?.mobile)

/** Get app theme */
export async function getTheme() {
  const theme = app.settings.theme

  return theme === 'dark'
    ? 'dark'
    : theme === 'system'
      ? isElectron
        ? (await numara.isDark())
          ? 'dark'
          : 'light'
        : window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
      : 'light'
}

/** Check window size. */
export async function checkSize() {
  const appWrapperRect = dom.appWrapper.getBoundingClientRect()
  const appWrapperWidth = Math.round(appWrapperRect.width)
  const appWrapperHeight = Math.round(appWrapperRect.height)
  const widthDiff = Math.abs(appWrapperWidth - DEFAULT_APP_WIDTH)
  const heightDiff = Math.abs(appWrapperHeight - DEFAULT_APP_HEIGHT)
  const isAppWrapperResized = widthDiff > 1 || heightDiff > 1

  dom.resetSizeButton.style.display =
    isElectron && isAppWrapperResized && !(await numara.isMaximized()) ? 'block' : 'none'
}

/** Get standard BCP 47 standard locale tag based on selection */
export function getAppLocale(settings = app.settings) {
  return coreGetAppLocale(settings)
}

/** Get standard BCP 47 standard locale tag based on system preference */
export function getSystemLocale(settings = app.settings) {
  return coreGetSystemLocale(settings)
}

/** Initialize app update listener */
export function initAppUpdate() {
  if (!isElectron) return

  const updateStatusMessage = (message) => {
    dom.dialogAboutUpdateStatus.textContent = message
  }

  numara.updateStatus((status, version, progress) => {
    switch (status) {
      case 'checking':
        updateStatusMessage('Checking for updates...')
        break
      case 'available':
        dom.notificationDot.style.display = 'block'
        dom.notificationDot.setAttribute('uk-tooltip', `New version available`)
        break

      case 'notAvailable':
        updateStatusMessage('Numara is up to date.')
        break
      case 'downloading':
        updateStatusMessage(`Downloading update... (${Math.round(progress?.percent ?? 0)}%)`)
        break
      case 'downloaded': {
        const notice = `New version ${version} is ready to install.`

        updateStatusMessage(notice)

        dom.updateButton.style.display = 'inline-block'
        dom.updateButton.onclick = () => numara.updateApp()
        break
      }
      case 'error':
        updateStatusMessage('Error while checking for updates.')
        break
      default:
        updateStatusMessage('Unable to check for update.')
    }
  })
}

/** Check for app update */
export function checkAppUpdate() {
  if (!isElectron) return
  numara.checkUpdate()
}

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds have elapsed
 * @param {Function} func The function to debounce
 * @param {number} wait The delay in milliseconds
 * @returns {Function} The debounced function
 */
export function debounce(func, wait) {
  let timeout = null
  const debounced = function (...args) {
    const context = this

    clearTimeout(timeout)
    timeout = setTimeout(() => {
      timeout = null
      func.apply(context, args)
    }, wait)
  }

  debounced.flush = function () {
    if (timeout) {
      clearTimeout(timeout)
      timeout = null
      func()
    }
  }

  debounced.cancel = function () {
    if (timeout) {
      clearTimeout(timeout)
      timeout = null
    }
  }

  return debounced
}
